import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { Modal, PrimaryButton, SecondaryButton } from '../../components';

import css from './ConfirmActionModal.module.css';

/**
 * Generic confirm/cancel modal for irreversible or significant
 * actions. Renders a title, a description, and two buttons: a
 * "keep / go back" dismiss and a confirm that fires the action.
 *
 * Reusable across the app — pass the copy in as translation ids and
 * wire `onConfirm` to whatever should happen. The confirm button can
 * be styled as destructive (default) for cancel/delete-type actions.
 *
 * Copy is supplied as translation ids (not raw strings) so callers
 * stay consistent with the rest of the i18n setup. `confirmInProgress`
 * + `confirmError` let the modal reflect an async dispatch's state
 * without the caller closing it prematurely.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - Unique modal id (required by Modal).
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {Function} props.onClose - Close/dismiss handler.
 * @param {Function} props.onManageDisableScrolling - Scroll-lock handler from the page.
 * @param {Function} props.onConfirm - Fired when the confirm button is clicked.
 * @param {string} props.titleId - Translation id for the heading.
 * @param {string} props.descriptionId - Translation id for the body copy.
 * @param {Object} [props.descriptionValues] - Values for the description FormattedMessage.
 * @param {string} props.confirmLabelId - Translation id for the confirm button.
 * @param {string} props.cancelLabelId - Translation id for the dismiss button.
 * @param {boolean} [props.confirmInProgress] - Spinner state on the confirm button.
 * @param {boolean} [props.confirmDisabled] - Disable the confirm button.
 * @param {Object} [props.confirmError] - When set, shows confirmErrorId below the buttons.
 * @param {string} [props.confirmErrorId] - Translation id for the error line.
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 */
const ConfirmActionModal = props => {
  const {
    className,
    rootClassName,
    id,
    isOpen = false,
    onClose,
    onManageDisableScrolling,
    onConfirm,
    titleId,
    descriptionId,
    descriptionValues,
    confirmLabelId,
    cancelLabelId,
    confirmInProgress = false,
    confirmDisabled = false,
    confirmError,
    confirmErrorId,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <p className={css.modalTitle}>
        <FormattedMessage id={titleId} />
      </p>
      <p className={css.modalMessage}>
        <FormattedMessage id={descriptionId} values={descriptionValues} />
      </p>

      {confirmError && confirmErrorId ? (
        <p className={css.errorMessage}>
          <FormattedMessage id={confirmErrorId} />
        </p>
      ) : null}

      <div className={css.actions}>
        <SecondaryButton
          className={css.cancelButton}
          type="button"
          onClick={onClose}
          disabled={confirmInProgress}
        >
          <FormattedMessage id={cancelLabelId} />
        </SecondaryButton>
        <PrimaryButton
          className={css.confirmButton}
          type="button"
          onClick={onConfirm}
          inProgress={confirmInProgress}
          disabled={confirmDisabled || confirmInProgress}
        >
          <FormattedMessage id={confirmLabelId} />
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default ConfirmActionModal;
